import dedent from "dedent";
import type { NotificationData, NotificationText } from "../supabase/ports/index";
import type { BackOfficeNotifications, MobileAppNotifications } from "../supabase/ports/notification";
import type { Database } from "../types/supabase.gen";
import { getFullName } from "./people";
import {
  translateContractStatus,
  translateDefinitiveEndType,
  translateDocumentType,
  translateProfessionalCardRefusalReason,
} from "./translate";

const externalReviewerLabel: Record<Database["public"]["Enums"]["document_external_reviewer"], string> = {
  titre_de_sejour_prefecture_police_paris: "La préfecture de police de Paris",
};

export type { NotificationData, NotificationText };

/**
 * Notification Content
 * ------------------
 * This file handles the formatting of notifications for both mobile app and back office.
 *
 * Structure:
 * 1. Type definitions for notification formatters - we separate mobile app and back office.
 * 2. Helper functions for formatting text and links
 * 3. Main generateNotificationText function that will look up on the maps (back-office formatters & mobile app formatters)
 * 4. Mobile app notification formatters (SMS-style notifications)
 * 5. Back office notification formatters (HTML-style notifications with links)
 *
 */

const EMPTY = "-";

// Types the function used for format, with a generic Type argument
type FormatterFn<Type> = (
  notificationData: Extract<NotificationData, { type: Type }>["data"],
  baseUrl?: string
) => NotificationText;

// Extract Type and gives it to the FormatterFn, for mobile app and BackOffice
type MobileAppNotificationFormatters = {
  [Type in Extract<MobileAppNotifications["type"], string>]: FormatterFn<Type>;
};
type BackOfficeNotificationFormatters = {
  [Type in Extract<BackOfficeNotifications["type"], string>]: FormatterFn<Type>;
};

const formatOptionalValueWithFunc = (value: unknown, func: (...args: any[]) => string) => {
  return value ? func(value) : EMPTY;
};

// The back-office renders a body as HTML: text a team member typed is escaped before it lands there.
const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const formatLink = (path: string, text: string, baseUrl = "") =>
  baseUrl ? `<a href="${baseUrl}${path}">${text}</a>` : text;

const formatPldBatchErrorLines = (errors: { talentFullName: string | null; reason: string }[] | undefined) =>
  (errors ?? [])
    .map((error) => (error.talentFullName ? `• ${error.talentFullName} — ${error.reason}` : `• ${error.reason}`))
    .join("\n");

const formatTimesheetPldBatchSendCompletedTitle = ({
  successCount,
  errorCount,
  skippedCount,
}: {
  successCount: number;
  errorCount: number;
  skippedCount: number;
}) => {
  const skippedLabel = skippedCount > 0 ? `${skippedCount} déjà dans PLD` : null;

  if (errorCount > 0) {
    const parts = [
      `${successCount} succès`,
      ...(skippedLabel ? [skippedLabel] : []),
      `${errorCount} erreur${errorCount > 1 ? "s" : ""}`,
    ];
    return `⚠️ Envoi PLD des relevés terminé (${parts.join(", ")})`;
  }

  if (successCount === 0 && skippedCount > 0) {
    return `✅ ${skippedCount} semaine${skippedCount > 1 ? "s" : ""} déjà dans PLD`;
  }

  const sentLabel = `✅ ${successCount} semaine${successCount > 1 ? "s" : ""} envoyée${successCount > 1 ? "s" : ""} sur PLD`;
  return skippedLabel ? `${sentLabel} (${skippedLabel})` : sentLabel;
};

export const generateNotificationText = ({ type, data }: NotificationData, baseUrl?: string): NotificationText => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const { title, body } = notificationTextFormatters[type](data as any, baseUrl);

  return {
    title,
    body: dedent(body),
  };
};

const mobileAppNotificationTextFormatters: MobileAppNotificationFormatters = {
  mobile_app__talent_new_final_pay_slip: ({ monthYear, isPushNotification }) => {
    const smsBody = `Ton bulletin de paie du mois de ${monthYear} est maintenant disponible. Tu peux le consulter et le télécharger depuis ton espace. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ton bulletin de paie du mois de ${monthYear} est maintenant disponible.`;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Tu as reçu ta fiche de paie",
      body,
    };
  },

  mobile_app__talent_contract_signed: ({ client_name: clientName, isPushNotification }) => {
    const smsBody = `Ton contrat chez ${clientName} est bien signé. Retrouve-le dans tes documents sur l'application. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ton contrat chez ${clientName} est bien signé. `;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Contrat signé",
      body,
    };
  },

  mobile_app__talent_timesheet_reminder: ({
    week_number: weekNumber,
    has_auth_access: hasAuthAccess,
    navigateTo,
    isPushNotification,
  }) => {
    const smsBody = `Ton relevé d'heures (semaine ${weekNumber}) n'est toujours pas signé. ${
      hasAuthAccess ? `Retrouve le ici: ${navigateTo}` : "Télécharge l'application asap.work pour le signer"
    }. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ton relevé d'heures (semaine ${weekNumber}) n'est toujours pas signé.`;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Relevé d'heures à signer",
      body,
    };
  },

  mobile_app__timesheet_fully_approved: ({
    client_name: clientName,
    week_number: weekNumber,
    navigateTo,
    isPushNotification,
  }) => {
    const smsBody = `Ton relevé d'heures (semaine ${weekNumber}) a été approuvé par ${clientName}. Retrouve le ici: ${navigateTo}. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ton relevé d'heures (semaine ${weekNumber}) a été approuvé par ${clientName}.`;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Relevé d'heures approuvé",
      body,
    };
  },

  mobile_app__timesheet_rejected_by_client: ({
    client_name: clientName,
    week_number: weekNumber,
    navigateTo,
    isPushNotification,
  }) => {
    const smsBody = `Ton relevé d'heures (semaine ${weekNumber}) n'a pas été validé par ${clientName}. Modifie le ici: ${navigateTo}. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ton relevé d'heures (semaine ${weekNumber}) n'a pas été validé par ${clientName}.`;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Relevé d'heures refusé par le client",
      body,
    };
  },

  mobile_app__timesheet_reset_signatures: ({ week_number: weekNumber, navigateTo, isPushNotification }) => {
    const smsBody = `Ton relevé d'heures (semaine ${weekNumber}) a été réinitialisé. Complète le ici: ${navigateTo}. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ton relevé d'heures (semaine ${weekNumber}) a été réinitialisé.`;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Relevé d'heures réinitialisé",
      body,
    };
  },

  mobile_app__expense_claim_approved: ({ amount, isPushNotification }) => {
    const smsBody = `Ta note de frais de ${amount}€ a été approuvée.  Retrouve la sur l'application. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ta note de frais de ${amount}€ a été approuvée.`;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Note de frais approuvée",
      body,
    };
  },

  mobile_app__expense_claim_refused: ({ amount, isPushNotification }) => {
    const smsBody = `Ta note de frais de ${amount}€ a été refusée. Retrouve la sur l'application. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ta note de frais de ${amount}€ a été refusée. `;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Note de frais refusée",
      body,
    };
  },

  mobile_app__talent_contract_unsigned_reminder: ({
    company_name: companyName,
    has_auth_access: hasAuthAccess,
    navigateTo,
    isPushNotification,
  }) => {
    const smsBody = `Ton contrat ${companyName ? `chez ${companyName}` : ``} n'est pas signé. ${
      hasAuthAccess ? `Retrouve le ici: ${navigateTo}` : "Télécharge l'application asap.work pour le retrouver"
    }. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ton contrat ${companyName ? `chez ${companyName}` : ``} n'est pas signé.`;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Contrat à signer",
      body,
    };
  },

  mobile_app__expense_claim_canceled: ({ amount, isPushNotification }) => {
    const smsBody = `Ta note de frais de ${amount}€ a été annulée. Retrouve la sur l'application. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ta note de frais de ${amount}€ a été annulée. `;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Note de frais annulée",
      body,
    };
  },

  mobile_app__talent_document_rejected: ({ documentType, isPushNotification }) => {
    const documentName = translateDocumentType.fr[documentType];
    const smsBody = `Ton document (${documentName}) a été refusé. Consulte la raison et remplace-le depuis l'application. STOP au [STOP_CODE]`;
    const pushNotificationBody = `Ton document (${documentName}) a été refusé. Consulte la raison et remplace-le.`;

    const body = isPushNotification ? pushNotificationBody : smsBody;
    return {
      title: "Document refusé",
      body,
    };
  },
  mobile_app__professional_card_photo_reminder: ({ consultantFullName, isPushNotification }) => {
    const recipient = consultantFullName
      ? `à ${consultantFullName}, ton consultant asap.work`
      : "à ton consultant asap.work";
    const request = `Pour finaliser ta carte BTP, envoie une photo de toi de face sur fond blanc ${recipient}.`;
    return {
      title: "Photo pour ta carte BTP",
      body: isPushNotification ? request : `${request} STOP au [STOP_CODE]`,
    };
  },
  // The app asks for the photo again on its own; an SMS reader sends it to their consultant.
  mobile_app__professional_card_photo_rejected: ({ consultantFullName, isPushNotification }) => {
    const rejected = "Ta photo pour la carte BTP n'a pas pu être acceptée.";
    if (isPushNotification) {
      return {
        title: "Photo pour ta carte BTP",
        body: `${rejected} Refais-en une de face sur fond blanc, en suivant les consignes de l'application.`,
      };
    }
    const recipient = consultantFullName
      ? `à ${consultantFullName}, ton consultant asap.work`
      : "à ton consultant asap.work";
    return {
      title: "Photo pour ta carte BTP",
      body: `${rejected} Envoie une nouvelle photo de toi de face sur fond blanc ${recipient}. STOP au [STOP_CODE]`,
    };
  },
};

const backOfficeNotificationTextFormatters: BackOfficeNotificationFormatters = {
  first_contract_signed_by_talent: ({ navigateTo, talent, owner, contract }, baseUrl) => ({
    title: `🚀 Premier contrat pour ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `
  ${formatLink(navigateTo, "Le contrat", baseUrl)} :
    - Consultant : ${owner.full_name}
    - Date de début de mission : ${contract.start_date}
    - Entreprise : ${contract.agency?.name} (${contract.agency?.formatted_address || EMPTY})
    - Métier : ${contract.job_name}

  ${formatLink(`/talents/${talent.id}`, "L'intérimaire", baseUrl)} :
    - Date de naissance : ${talent.birth_date}
    - Email : ${talent.email}
    - Téléphone : ${talent.phone_number}
    - Adresse : ${talent.address?.formatted}
  `,
  }),
  approved_recruitment_office_invoice_request: ({ navigateTo }, baseUrl) => ({
    title: `💸 Une facture CAB a été validée`,
    body: formatLink(navigateTo, "Facture finale à créer", baseUrl),
  }),
  failed_advance_payment_request: ({ navigateTo, talent, creation_date: creationDate }, baseUrl) => ({
    title: "❌ Échec de la demande d'acompte sur salaire !",
    body: `Vérifier ${formatLink(navigateTo, "la demande", baseUrl)} lié à l'adresse e-mail ${talent.email || EMPTY} datant du ${creationDate}`,
  }),
  done_recruitment_office_invoice_request: ({ navigateTo }, baseUrl) => ({
    title: "💸 Ta facture CAB finale est prête !",
    body: `Tu peux la consulter en ${formatLink(navigateTo, "cliquant ici", baseUrl)}`,
  }),
  pending_recruitment_office_invoice_request: ({ navigateTo }, baseUrl) => ({
    title: "💸 Validez votre facture CAB !",
    body: `Vous avez un ${formatLink(navigateTo, "brouillon de facture CAB", baseUrl)} en attente de validation.`,
  }),
  recruitment_office_payment_paid: ({ navigateTo, amount, companyName, talentFullName, isPercentage }, baseUrl) => ({
    title: "💸 Paiement reçu pour un de tes CAB",
    body: `Un paiement de ${amount} ${isPercentage ? "% des honoraires" : "€"} a été effectué pour le CAB entre ${talentFullName} et l'entreprise ${companyName}. ${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  new_advance_payment_request: (
    { navigateTo, talent, team_member: teamMember, amount, isMaxAmountRequested },
    baseUrl
  ) => {
    let textAmount = "ERREUR (montant non défini)";
    if (isMaxAmountRequested) {
      textAmount = "du montant maximum autorisé";
    } else if (amount) {
      textAmount = `de ${amount}€`;
    }
    return {
      title: `💸 ${formatOptionalValueWithFunc(talent, getFullName)} a fait une demande d'acompte sur salaire ${textAmount} !`,
      body: `Merci de bien veiller à ce que ses RH aient été ajoutés sinon son acompte pourra être refusé. Son consultant est ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "clique ici", baseUrl)}`,
    };
  },
  new_contract_to_process: ({ navigateTo, is_amendment: isAmendment, team_member: teamMember, talent }, baseUrl) => ({
    title: `🔥 Nouvelle demande ${isAmendment ? "d'avenant" : "de contrat"} pour ${formatOptionalValueWithFunc(talent, getFullName)} !`,
    body: `Créé par ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "clique ici", baseUrl)}`,
  }),
  new_contract_to_send: ({ navigateTo, talentFullName, is_amendment: isAmendment }, baseUrl) => ({
    title: `💌 Le ${isAmendment ? "nouvel avenant" : "nouveau contrat"} de ${talentFullName} est prêt à être envoyé !`,
    body: `${formatLink(navigateTo, "Clique ici", baseUrl)} pour ${isAmendment ? "l'envoyer" : "relire les documents"}.`,
  }),
  new_definitive_end_on_contract_to_process: ({ navigateTo, owner, talent }, baseUrl) => ({
    title: `👋 Nouvelle demande de fin définitive pour ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `Créée par ${formatOptionalValueWithFunc(owner, getFullName)}: ${formatLink(navigateTo, "clique ici", baseUrl)}.`,
  }),
  new_expense_claim_request_to_validate: ({ navigateTo, team_member: teamMember }, baseUrl) => ({
    title: `🖍️ Nouvelle demande de note de frais à valider pour ${formatOptionalValueWithFunc(teamMember, getFullName)}`,
    body: `La consulter ici : ${formatLink(navigateTo, "clique ici", baseUrl)}`,
  }),
  new_expense_claim_request_to_process: ({ navigateTo, talentFullName, team_member: teamMember }, baseUrl) => ({
    title: `🤑 Nouvelle demande de note de frais pour ${talentFullName ? `le talent ${talentFullName}` : "un talent"} !`,
    body: `Créé par ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "clique ici", baseUrl)}`,
  }),
  new_medical_checkup_request: ({ navigateTo, talent, team_member: teamMember }, baseUrl) => ({
    title: `🩺 Nouvelle demande de visite médicale à traiter pour ${formatOptionalValueWithFunc(talent, getFullName)} !`,
    body: `Créée par ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "clique ici", baseUrl)} `,
  }),
  new_onboarding_app_needed: ({ navigateTo, talent, owner }, baseUrl) => ({
    title: `📱 Demande d'onboarding app pour ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `Une ${formatLink(navigateTo, "nouvelle demande d'onboarding", baseUrl)} vient d'être créée par ${formatOptionalValueWithFunc(owner, getFullName)}`,
  }),
  new_professional_card_request: ({ navigateTo, talent, team_member: teamMember }, baseUrl) => ({
    title: `🪪 Nouvelle demande de carte BTP à traiter pour ${formatOptionalValueWithFunc(talent, getFullName)} !`,
    body: `Créée par ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "clique ici", baseUrl)}`,
  }),
  new_qualification_training_request: ({ navigateTo, talent, team_member: teamMember }, baseUrl) => ({
    title: `🎓 Nouvelle demande d'habilitation à traiter pour ${formatOptionalValueWithFunc(talent, getFullName)}  !`,
    body: `Créée par ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "clique ici", baseUrl)} `,
  }),
  new_recruitment_office_invoice_request: ({ navigateTo }, baseUrl) => ({
    title: "💸 Création de CAB ! ",
    body: `${formatLink(navigateTo, "Ajoutez une facture", baseUrl)} pour relecture par le consultant`,
  }),
  refused_recruitment_office_invoice_request: ({ navigateTo }, baseUrl) => ({
    title: "💸 Brouillon de facture CAB refusée !",
    body: `${formatLink(navigateTo, "Cliquer ici pour la modifier", baseUrl)} la modifier.`,
  }),
  talent_offboarding_needed: ({ navigateTo, contract, owner_full_name: ownerFullName, talent }, baseUrl) => ({
    title: `💔 Fin définitive de ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `
  ${formatLink(navigateTo, "Le contrat", baseUrl)} :
    - Consultant : ${ownerFullName}
    - Date de fin de mission prévu : ${contract.end_date}
    - Date de fin de mission réel : ${contract.real_end_date}
    - Raison de la fin définitive : ${contract.definitive_end_type ? translateDefinitiveEndType.fr[contract.definitive_end_type] : "Inconnu"}
      - Détails (optionnel) : ${contract.definitive_end_details || EMPTY}

  ${formatLink(`/talents/${talent.id}`, "L'intérimaire", baseUrl)} :
    - Email : ${talent.email}
    - Adresse : ${talent.address?.formatted}
  `,
  }),
  new_qualification_training_request_invoicing_document: (
    { navigateTo, qualificationCenterName, talent },
    baseUrl
  ) => ({
    title: `🎓 Nouveau document de facturation liée à une demande d'habilitation`,
    body: `${formatLink(navigateTo, "Les factures de la formation", baseUrl)} de ${formatOptionalValueWithFunc(talent, getFullName)} du centre de formation ${qualificationCenterName} sont disponibles.`,
  }),
  new_qualification_training_request_certificate_document: ({ navigateTo, talent }, baseUrl) => ({
    title: `🎓 Nouveau certificat liée à une demande d'habilitation`,
    body: `${formatLink(navigateTo, "Le certificat de formation", baseUrl)} de ${formatOptionalValueWithFunc(talent, getFullName)} est disponible.`,
  }),
  cancel_medical_checkup_request_on_definitive_contract_end: (
    { navigateTo, medicalCenterName, status, talent, wsm },
    baseUrl
  ) => ({
    title: `❌ Nouvelle demande de visite médicale ${status === "approved" ? "à annuler" : "annulée"}`,
    body:
      status === "todo" || status === "pending"
        ? `La fin définitive de contrat de ${formatOptionalValueWithFunc(talent, getFullName)} a été validée et ${formatLink(navigateTo, "une demande de visite médicale", baseUrl)} avait été initiée, elle est désormais annulée:
          - WSM : ${formatOptionalValueWithFunc(wsm, getFullName)}`
        : `Une fin définitive de contrat vient d'être validée pour ${formatOptionalValueWithFunc(talent, getFullName)} et ${formatLink(navigateTo, "une de visite médicale", baseUrl)} est prévue veuillez l'annuler:
          - WSM : ${formatOptionalValueWithFunc(wsm, getFullName)}
          - Centre de visite médicale : ${medicalCenterName}`,
  }),
  cancel_ppe_request_on_definitive_contract_end: ({ navigateTo, talent, status }, baseUrl) => ({
    title: `❌ Nouvelle demande d'EPI ${status === "approved" ? "à refuser" : "refusée"}`,
    body:
      status === "todo" || status === "pending"
        ? `La fin définitive de contrat de ${formatOptionalValueWithFunc(talent, getFullName)} a été validée et ${formatLink(navigateTo, "une demande d'EPI", baseUrl)} avait été initiée, elle est désormais refusée.`
        : `Une fin définitive de contrat vient d'être validée pour ${formatOptionalValueWithFunc(talent, getFullName)} et ${formatLink(navigateTo, "une demande d'EPI", baseUrl)} est prévue veuillez la refuser.`,
  }),
  cancel_qualif_train_req_on_definitive_contract_end: ({ navigateTo, talent, qualificationCenterName }, baseUrl) => ({
    title: `❌ Nouvelle demande d'habilitation à annuler`,
    body: `Une fin définitive de contrat vient d'être validée pour ${formatOptionalValueWithFunc(talent, getFullName)} et ${formatLink(navigateTo, "une demande d'habilitation", baseUrl)} du centre de formation ${qualificationCenterName} est prévue veuillez l'annuler.`,
  }),
  end_of_company_insurance_request: ({ navigateTo, status }, baseUrl) => ({
    title: `🛡️ Demande de garantie ${status.includes("approved") ? "acceptée" : "refusée"} !`,
    body: formatLink(navigateTo, "Le détail sur le BO", baseUrl),
  }),
  new_company_insurance_request: ({ navigateTo }, baseUrl) => ({
    title: `🛡️ Nouvelle demande de garantie !`,
    body: formatLink(navigateTo, "Envoyer à Groupama", baseUrl),
  }),
  failed_website_job_posting: ({ navigateTo }, baseUrl) => ({
    title: `❌ Échec de la demande de publication d'offre d'emploi sur le site !`,
    body: `Quelqu'un est probablement sur webflow, il faut sûrement republier le site en production (${formatLink(navigateTo, "la mission", baseUrl)}).`,
  }),
  first_contract_signed_by_independent: ({ navigateTo, owner_full_name: ownerFullName }, baseUrl) => ({
    title: `🚀 Premier contrat signé par l'indépendant ${ownerFullName}`,
    body: `${formatLink(navigateTo, "Félicitations", baseUrl)} ! Il est temps de partager avec lui nos process WSM.`,
  }),
  new_qualification_training_commitment: ({ navigateTo, talent, wsm }) => ({
    title: `🎓 Dédit de formation à faire remplir !`,
    body: `Convocation uploadée pour la demande de formation de <strong>${formatOptionalValueWithFunc(talent, getFullName)}</strong>.
  Pensez à lui faire remplir le dédit de formation : <a href="${navigateTo}">clique ici</a>.
  Ceci est essentiel pour ce type de formation.
  Vous pouvez faire une copie du document afin de l'éditer.
  Une fois signée via YouSign, envoyez au WSM en charge de la demande: ${formatOptionalValueWithFunc(wsm, getFullName)}`,
  }),
  new_contract_document_pre_employment_declaration: ({ navigateTo, talent }, baseUrl) => ({
    title: `🎒 Nouvelle DPAE liée à un contrat !`,
    body: `Une ${formatLink(navigateTo, "nouvelle déclaration préalable à l'embauche", baseUrl)} a été uploadée :
  - Intérimaire : ${formatOptionalValueWithFunc(talent, getFullName)}`,
  }),
  new_mission_to_post_on_job_boards: ({ navigateTo, teamMember }, baseUrl) => ({
    title: `🙏 Une tit' multidiffusion pour ${formatOptionalValueWithFunc(teamMember, getFullName)} `,
    body: formatLink(navigateTo, "Clique ici", baseUrl),
  }),
  new_professional_card_request_document_uploaded: ({ navigateTo, talent }, baseUrl) => ({
    title: `🪪 Une nouvelle carte BTP a été uploadée suite à ta demande`,
    body: `Retrouve la carte BTP provisoire de ${formatOptionalValueWithFunc(talent, getFullName)} ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  professional_card_photo_missing: ({ navigateTo, talent, reason }, baseUrl) => ({
    title: `📸 Photo toujours manquante pour la carte BTP de ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body:
      reason === "talent_unreachable"
        ? `L'intérimaire n'a ni l'application ni numéro de téléphone : récupère sa photo toi-même et ajoute-la ${formatLink(navigateTo, "sur le back-office", baseUrl)}`
        : `Les relances sont restées sans réponse : récupère la photo auprès de l'intérimaire et ajoute-la ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  professional_card_request_completed: ({ navigateTo, talent }, baseUrl) => ({
    title: `📸 La demande de carte BTP de ${formatOptionalValueWithFunc(talent, getFullName)} est complète`,
    body: `La photo est arrivée : la demande peut être traitée ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  professional_card_request_resubmitted: ({ navigateTo, talent, team_member: teamMember }, baseUrl) => ({
    title: `🪪 Demande de carte BTP resoumise pour ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `Corrigée par ${formatOptionalValueWithFunc(teamMember, getFullName)} : la demande est à traiter ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  professional_card_photo_rejected: ({ navigateTo, talent, comment }, baseUrl) => ({
    title: `📸 Photo refusée pour la carte BTP de ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `La photo n'est pas conforme${comment ? ` (« ${escapeHtml(comment)} »)` : ""} : une nouvelle photo est demandée à l'intérimaire. ${formatLink(navigateTo, "Voir la demande", baseUrl)}`,
  }),
  professional_card_request_refused: ({ navigateTo, talent, reason, comment }, baseUrl) => ({
    title: "🪪 Ta demande de carte BTP a été refusée",
    body: `Demande de ${formatOptionalValueWithFunc(talent, getFullName)} refusée : ${translateProfessionalCardRefusalReason.fr[reason]}${comment ? ` (« ${escapeHtml(comment)} »)` : ""}.\nCorrige la demande puis resoumets-la : ${formatLink(navigateTo, "voir la demande", baseUrl)}`,
  }),
  archived_job_offer: ({ navigateTo }, baseUrl) => ({
    title: `👁️ Une mission a été archivée`,
    body: `Retrouve la ${formatLink(navigateTo, "ici", baseUrl)}`,
  }),
  new_contract_signed: ({ navigateTo, talent, is_amendment: isAmendment, signer }, baseUrl) => ({
    title: `🍾 ${isAmendment ? "L'avenant" : "Le contrat"} de ${formatOptionalValueWithFunc(talent, getFullName)} vient d'être signé par ${signer} !`,
    body: `${formatLink(navigateTo, "Clique ici", baseUrl)} pour le consulter.`,
  }),
  new_contract_declined: ({ navigateTo, talent, is_amendment: isAmendment, signer, declineReason }, baseUrl) => ({
    title: `😭 ${isAmendment ? "L'avenant" : "Le contrat"} de ${formatOptionalValueWithFunc(talent, getFullName)} vient d'être refusé par ${signer}: "${declineReason}"!`,
    body: `${formatLink(navigateTo, "Clique ici", baseUrl)} pour le consulter.`,
  }),
  expiring_document: ({ navigateTo, talentFullName, documentType, expiredAt }, baseUrl) => ({
    title: `‼️ Le ${documentType} de ${talentFullName} ${expiredAt === "est expiré" ? expiredAt : "arrive à expiration " + expiredAt} !`,
    body: `${formatLink(navigateTo, "Clique ici", baseUrl)} pour consulter son profil.`,
  }),
  new_commercial_condition_created: ({ navigateTo, owner, workspace }, baseUrl) => ({
    title: `📝 Nouvelle condition commerciale ${workspace === "internal" ? "" : "à valider"}`,
    body: `Créée par ${formatOptionalValueWithFunc(owner, getFullName)} : ${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  each_commercial_condition_validated: ({ navigateTo, company }, baseUrl) => ({
    title: `👌 Condition commerciale pour ${company} validée !`,
    body: `${formatLink(navigateTo, "Clique ici", baseUrl)} pour la consulter.`,
  }),
  commercial_conditions_is_signed: ({ navigateTo, company }, baseUrl) => ({
    title: `👌 Condition commerciale pour ${company} signée !`,
    body: `${formatLink(navigateTo, "Clique ici", baseUrl)} pour la consulter.`,
  }),
  commercial_conditions_is_declined: ({ navigateTo, company }, baseUrl) => ({
    title: `❌ Condition commerciale pour ${company} refusée !`,
    body: `${formatLink(navigateTo, "Clique ici", baseUrl)} pour la consulter.`,
  }),
  new_contract_to_validate: ({ navigateTo, owner }, baseUrl) => ({
    title: `📝 ${formatOptionalValueWithFunc(owner, getFullName)} a créé une demande de contrat !`,
    body: `Vérifie que tout est correct : ${formatLink(navigateTo, "Relis-le ici", baseUrl)}`,
  }),
  modified_contract_to_validate: ({ navigateTo, owner }, baseUrl) => ({
    title: `💬 ${formatOptionalValueWithFunc(owner, getFullName)} a modifié le contrat`,
    body: `Vérifie que tout est correct : ${formatLink(navigateTo, "Relis-le ici", baseUrl)}`,
  }),
  new_contract_to_validate_information: ({ navigateTo, consultant }, baseUrl) => ({
    title: `📝 ${formatOptionalValueWithFunc(consultant, getFullName)} a une demande de contrat indépendant à relire`,
    body: `Consulte le contrat ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  new_ppe_request: ({ navigateTo, team_member: teamMember, talent }, baseUrl) => ({
    title: `⛑️ Nouvelle demande d'EPI pour ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `Créée par ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "clique ici", baseUrl)}`,
  }),
  new_ppe_request_from_app: ({ navigateTo, team_member: teamMember, talent }, baseUrl) => ({
    title: `⛑️ Nouvelle demande d'EPI pour ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `À vérifier et confirmer par ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "clique ici", baseUrl)}`,
  }),
  new_ppe_request_validated: ({ navigateTo, team_member: teamMember, talent }, baseUrl) => ({
    title: `⛑️ Nouvelle demande d'EPI pour ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `Confirmée par ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "clique ici", baseUrl)}`,
  }),
  medical_checkup_request_pending: ({ navigateTo, talent }, baseUrl) => ({
    title: `🚑 La visite médicale de ${formatOptionalValueWithFunc(talent, getFullName)} est en cours de traitement !`,
    body: ` ${formatLink(navigateTo, "Clique ici", baseUrl)} pour voir le détail.`,
  }),
  medical_checkup_request_done: ({ navigateTo, talent }, baseUrl) => ({
    title: `👌 La visite médicale de ${formatOptionalValueWithFunc(talent, getFullName)} est terminée !`,
    body: ` ${formatLink(navigateTo, "Clique ici", baseUrl)} pour voir le détail.`,
  }),
  medical_checkup_request_approved: ({ navigateTo, talent, convocationDate }, baseUrl) => ({
    title: `🩺 La visite médicale de ${formatOptionalValueWithFunc(talent, getFullName)} est planifiée pour le ${convocationDate} et sa convocation est en ligne.`,
    body: ` ${formatLink(navigateTo, "Clique ici", baseUrl)} pour voir le détail.`,
  }),
  qualification_training_request_approved: ({ navigateTo, talent, convocationDate }, baseUrl) => ({
    title: `🎓 La demande d'habilitation de ${formatOptionalValueWithFunc(talent, getFullName)} est planifiée pour le ${convocationDate} et sa convocation est en ligne !`,
    body: ` ${formatLink(navigateTo, "Clique ici", baseUrl)} pour voir le détail.`,
  }),
  talent_plug_unknown_source_encountered: ({ talentPlugSource }) => ({
    title: `🚨 Source TalentPlug inconnue`,
    body: `Une candidature est arrivée avec la source \`${talentPlugSource}\`, qui n'est pas reconnue.`,
  }),
  team_member_offboarding_progressed: ({
    offboardedTeamMemberFullName,
    progressedByFullName,
    isBanned,
    isGoogleWorkspaceSuspended,
  }) => ({
    title: `👋 Offboarding de ${offboardedTeamMemberFullName}`,
    body: `${progressedByFullName} a avancé sur l'offboarding de ${offboardedTeamMemberFullName} :
      - Back Office : ${isBanned ? "banni" : "non banni"}
      - Google Workspace : ${isGoogleWorkspaceSuspended ? "suspendu" : "non suspendu"}`,
  }),
  new_rib_uploaded: ({ navigateTo, talentFullName, uploadedByFullName }, baseUrl) => ({
    title: `💳 Un nouveau RIB a été uploadé`,
    body: `${uploadedByFullName} a uploadé un nouveau RIB pour ${talentFullName}. Voir les détails : ${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  job_offer_republish: ({
    activeMissionArchived,
    activeMissionNotPublished,
    inactiveMissionPublished,
    activeMissionThatNeedToBeRepublished,
    totalActiveMissionThatNeedToBeRepublished,
  }) => ({
    title: `🔄 Republication des missions`,
    body: `
  - Missions actives non publiées : ${activeMissionNotPublished}
  - Missions actives archivées : ${activeMissionArchived}
  - Missions inactives publiées : ${inactiveMissionPublished}

  - Missions actives publiées y'a plus de 2 semaines qu'on va republier : ${activeMissionThatNeedToBeRepublished} / ${totalActiveMissionThatNeedToBeRepublished}
  `,
  }),
  new_request_push_marketing: ({ teamMember, navigateTo, talent }) => ({
    title: `🏋️‍♂️ Nouvelle demande de push marketing pour le talent ${formatOptionalValueWithFunc(talent, getFullName)} `,
    body: `Créée par ${formatOptionalValueWithFunc(teamMember, getFullName)} : <a href="${navigateTo}">clique ici</a>`,
  }),
  turned_off_push_marketing: ({ teamMember, navigateTo, talent }) => ({
    title: `✋ Demande pour désactiver un push marketing pour le talent ${formatOptionalValueWithFunc(talent, getFullName)}`,
    body: `Créée par ${formatOptionalValueWithFunc(teamMember, getFullName)} : <a href="${navigateTo}">clique ici</a>`,
  }),
  new_work_accident_request: ({ teamMember, navigateTo }, baseUrl) => ({
    title: "🚑 Nouvelle demande d'accident de travail",
    body: `Créée par ${formatOptionalValueWithFunc(teamMember, getFullName)} : ${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  work_accident_request_done: ({ talent, navigateTo }, baseUrl) => ({
    title: "✅ Demande d'accident du travail traitée",
    body: `Votre demande d'accident de travail pour ${formatOptionalValueWithFunc(talent, getFullName)} a été traitée : ${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  talent_documents_uploaded: ({ talentFullName, navigateTo }, baseUrl) => ({
    title: `📁 ${talentFullName} a uploadé tous ses documents !`,
    body: `Retrouve tous les documents de ${talentFullName} ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  expense_claim_to_review: ({ talentFullName, navigateTo }, baseUrl) => ({
    title: `💰 Une note de frais a été créée par ${talentFullName} sur l'application mobile`,
    body: `Complète et valide la demande : ${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  pay_tracking_completed: ({ talent, navigateTo, month }, baseUrl) => ({
    title: `🧾 La paie de ${formatOptionalValueWithFunc(talent, getFullName)} pour le mois de ${month} a été complétée, il est temps de la traiter`,
    body: `Retrouve la paie de ${formatOptionalValueWithFunc(talent, getFullName)} ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  pay_tracking_processed: ({ talent, navigateTo, month }, baseUrl) => ({
    title: `🔎 La paie de ${formatOptionalValueWithFunc(talent, getFullName)} pour le mois de ${month} a été traitée, il est temps de la vérifier`,
    body: `Retrouve la paie ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  pay_tracking_checked: ({ talent, navigateTo, month }, baseUrl) => ({
    title: `📬 La paie de ${formatOptionalValueWithFunc(talent, getFullName)} pour le mois de ${month} a été vérifiée et est prête à être envoyée`,
    body: `Retrouve la paie ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  timesheet_contact_signer_error: ({ navigateTo, company_name, contact_data, talent_name }, baseUrl) => ({
    title: `❌ Mauvaise coordonnées pour la signature chantier de ${talent_name}`,
    body: `Le talent ${talent_name} a utilisé de mauvaises coordonnées pour signer les RH avec ${company_name} (signature chantier).\nCoordonnées utilisées : email = ${contact_data.email}, nom = ${contact_data.first_name} ${contact_data.last_name}.\n Contrat concerné : ${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  expense_claim_request_to_confirm_reminder: ({ navigateTo, titleContent, bodyContent }, baseUrl) => ({
    title: `🚨 ACTION REQUISE - ${titleContent}`,
    body: `${bodyContent} ${formatLink(navigateTo, "👉 Direction le back-office!", baseUrl)}`,
  }),
  to_modify_by_owner: ({ navigateTo, talentFullName, commentAuthorFullName, is_amendment: isAmendment }, baseUrl) => ({
    title: `💬 Tu as une nouvelle demande de modification de la part de ${commentAuthorFullName} sur ${isAmendment ? "l'avenant" : "le contrat"} de ${talentFullName}`,
    body: `Rend toi ${formatLink(navigateTo, "sur le back-office", baseUrl)} pour corriger ${isAmendment ? "ton avenant" : "ton contrat"}`,
  }),
  contract_validated: ({ navigateTo, talentFullName, managerFullName }, baseUrl) => ({
    title: `✨ ${managerFullName} a validé ton contrat pour ${talentFullName}`,
    body: `Il est parti en traitement chez l'équipe admin. Si besoin, ${formatLink(navigateTo, "retrouve-le ici", baseUrl)}`,
  }),
  modified_contract_to_send: ({ navigateTo, talentFullName, is_amendment: isAmendment }, baseUrl) => ({
    title: `💌 Les documents ${isAmendment ? "de l'avenant" : "contractuels"} de ${talentFullName} sont corrigés et prêts.`,
    body: `Vérifie que tout est correct avant de les envoyer. ${formatLink(navigateTo, "Retrouve-les ici", baseUrl)}`,
  }),
  new_contract_to_modify_by_admin: (
    { navigateTo, talentFullName, consultantFullName, is_amendment: isAmendment },
    baseUrl
  ) => ({
    title: `✏️ Tu as une demande de modification sur ${isAmendment ? "l'avenant" : "le contrat"} de ${consultantFullName} pour ${talentFullName}`,
    body: `Tu peux consulter la demande ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  new_amendment_to_validate: ({ navigateTo, owner }, baseUrl) => ({
    title: `L'avenant de ${formatOptionalValueWithFunc(owner, getFullName)} attend ta validation`,
    body: `Relis et valide la demande ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  inform_manager_on_new_contract_created_without_validation: (
    { navigateTo, talentFullName, ownerFullName, isAmendment },
    baseUrl
  ) => ({
    title: `📝 ${ownerFullName} a créé une demande ${isAmendment ? "d'avenant" : "de contrat"} pour ${talentFullName} !`,
    body: `Si besoin, retrouve le formulaire ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  advance_payment_request_payment_failed: ({ navigateTo, talent, amount, errorReason }, baseUrl) => ({
    title: `❌ Paiement de l'acompte de ${amount}€ pour ${formatOptionalValueWithFunc(talent, getFullName)} échoué`,
    body: `Une erreur est survenue lors du paiement de l'acompte de ${amount}€ pour ${formatOptionalValueWithFunc(talent, getFullName)} : ${errorReason}. \nPour consulter la demande :${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  advance_payment_request_payment_refused: ({ navigateTo, talent, amount, errorReason }, baseUrl) => ({
    title: `❌ Paiement de l'acompte de ${amount ? `${amount}€` : "ERREUR (montant non défini)"} pour ${formatOptionalValueWithFunc(talent, getFullName)} a été refusé par l'équipe Admin`,
    body: `Raison: ${errorReason}. \nPour consulter la demande :${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  advance_payment_pld_batch_send_completed: (
    { navigateTo, successCount, errorCount, payPeriodLabel, errors },
    baseUrl
  ) => {
    const hasErrors = errorCount > 0;
    const title = hasErrors
      ? `⚠️ Envoi PLD terminé (${successCount} succès, ${errorCount} erreur${errorCount > 1 ? "s" : ""})`
      : `✅ ${successCount} acompte${successCount > 1 ? "s" : ""} envoyé${successCount > 1 ? "s" : ""} sur PLD`;
    const periodLine = `Période de paie : ${payPeriodLabel}.`;
    const linkLine = `Consulte les acomptes : ${formatLink(navigateTo, "Clique ici", baseUrl)}`;
    const errorLines = (errors ?? [])
      .map((error) => (error.talentFullName ? `• ${error.talentFullName} — ${error.reason}` : `• ${error.reason}`))
      .join("\n");

    return {
      title,
      body: errorLines ? `${periodLine}\n${errorLines}\n${linkLine}` : `${periodLine}\n${linkLine}`,
    };
  },
  third_party_notice_bulk_import_completed: (
    { navigateTo, successCount, errorCount, pageErrors, failureReason },
    baseUrl
  ) => {
    const linkLine = `Consulte les avis : ${formatLink(navigateTo, "Clique ici", baseUrl)}`;
    const pageLines = (pageErrors ?? [])
      .map((error) => {
        const pages =
          error.lastPage !== undefined && error.lastPage !== error.page
            ? `Pages ${error.page}-${error.lastPage}`
            : `Page ${error.page}`;
        return `• ${pages} — ${error.reason}`;
      })
      .join("\n");
    const detail = [failureReason, pageLines].filter((line) => line).join("\n");

    if (failureReason) {
      return {
        title: "❌ L'import ATD n'a pas pu démarrer",
        body: detail ? `${detail}\n${linkLine}` : linkLine,
      };
    }

    const hasErrors = errorCount > 0;
    const title = hasErrors
      ? `⚠️ Import ATD terminé (${successCount} succès, ${errorCount} erreur${errorCount > 1 ? "s" : ""})`
      : `✅ ${successCount} avis à tiers détenteur importé${successCount > 1 ? "s" : ""}`;

    return {
      title,
      body: detail ? `${detail}\n${linkLine}` : linkLine,
    };
  },
  timesheet_pld_batch_send_completed: ({ navigateTo, successCount, errorCount, skippedCount, errors }, baseUrl) => {
    const skipped = skippedCount ?? 0;
    const linkLine = `Consulte le suivi de paie : ${formatLink(navigateTo, "Clique ici", baseUrl)}`;
    const errorLines = formatPldBatchErrorLines(errors);

    return {
      title: formatTimesheetPldBatchSendCompletedTitle({ successCount, errorCount, skippedCount: skipped }),
      body: errorLines ? `${errorLines}\n${linkLine}` : linkLine,
    };
  },
  extension_amendment_pld_batch_send_completed: ({ navigateTo, successCount, errorCount, errors }, baseUrl) => {
    const hasErrors = errorCount > 0;
    const title = hasErrors
      ? `⚠️ Envoi PLD des avenants terminé (${successCount} succès, ${errorCount} erreur${errorCount > 1 ? "s" : ""})`
      : `✅ ${successCount} avenant${successCount > 1 ? "s" : ""} envoyé${successCount > 1 ? "s" : ""} sur PLD`;
    const linkLine = `Consulte les avenants de prolongation : ${formatLink(navigateTo, "Clique ici", baseUrl)}`;
    const errorLines = formatPldBatchErrorLines(errors);

    return {
      title,
      body: errorLines ? `${errorLines}\n${linkLine}` : linkLine,
    };
  },
  timesheet_reset_signatures: ({ talent, weekNumber, navigateTo }, baseUrl) => ({
    title: `🔄 Relevé d'heures réinitialisé`,
    body: `Le relevé d'heures de ${formatOptionalValueWithFunc(talent, getFullName)} pour la semaine ${weekNumber} a été réinitialisé. Consulte le relevé ${formatLink(navigateTo, "sur le back-office", baseUrl)}`,
  }),
  timesheet_reset_for_admin: ({ teamMemberFullName, talentFullName, monthYear, navigateTo }, baseUrl) => ({
    title: `🔄 Relevé d'heures réinitialisé`,
    body: `Un relevé d’heures pour la paie de ${monthYear} de ${talentFullName} a été réinitialisé par ${teamMemberFullName} : ${formatLink(navigateTo, "Lien vers la paie", baseUrl)}. \nIl devra être ressaisi dans PLD une fois la paie validée. `,
  }),
  medical_checkup_request_needed: ({ navigateTo, talentFullName }, baseUrl) => ({
    title: `🚑 Une visite médicale pour ${talentFullName} est nécessaire`,
    body: `${talentFullName} a travaillé plus de 30 jours sans visite médicale ! 😱 ${formatLink(navigateTo, "Clique ici", baseUrl)}`,
  }),
  timesheet_proof_uploaded_by_talent: ({ navigateTo, talentFullName }, baseUrl) => ({
    title: `📝 Un relevé d’heures papier a été ajouté pour le contrat de ${talentFullName}`,
    body: formatLink(navigateTo, "Clique ici pour le valider", baseUrl),
  }),
  document_external_review_rejected: ({ navigateTo, talent, documentType, reviewer }, baseUrl) => {
    const documentName = translateDocumentType.fr[documentType];
    const talentFullName = [talent.first_name, talent.last_name].filter(Boolean).join(" ") || EMPTY;
    return {
      title: `🚨 ${documentName} refusé — ${talentFullName}`,
      body: `${externalReviewerLabel[reviewer]} a refusé la demande de ${documentName} de ${talentFullName}. Ce talent a un contrat en cours ou à venir. <strong>La mission ne peut pas se poursuivre.</strong> Prenez les mesures nécessaires immédiatement. ${formatLink(navigateTo, "Voir les documents sur le BO", baseUrl)}`,
    };
  },
  // Allianz sends no motive with a NoCover/Rejected decision, so the absence is stated outright.
  credit_insurer_cover_refused: ({ navigateTo, companyName, requestedAmountLabel }, baseUrl) => ({
    title: `🚫 Couverture refusée — ${companyName}`,
    body: `Allianz a refusé de couvrir ${companyName} (montant demandé : ${requestedAmountLabel}) et ne communique pas la raison. Rapproche-toi de l'équipe finance pour décider de la suite. ${formatLink(navigateTo, "Voir la garantie crédit sur le BO", baseUrl)}`,
  }),
  credit_insurer_cover_approved: ({ navigateTo, companyName, requestedAmountLabel }, baseUrl) => ({
    title: `✅ Couverture accordée — ${companyName}`,
    body: `Allianz a accepté de couvrir ${companyName} (montant demandé : ${requestedAmountLabel}). ${formatLink(navigateTo, "Voir la garantie crédit sur le BO", baseUrl)}`,
  }),
  credit_insurer_cover_amount_changed: (
    { navigateTo, companyName, previousAmountLabel, newAmountLabel, isReduction },
    baseUrl
  ) => ({
    title: `${isReduction ? "📉" : "📈"} Couverture ${isReduction ? "réduite" : "augmentée"} — ${companyName}`,
    body: `Allianz couvre désormais ${companyName} à hauteur de ${newAmountLabel} (avant : ${previousAmountLabel}). ${formatLink(navigateTo, "Voir la garantie crédit sur le BO", baseUrl)}`,
  }),
  contract_canceled_by_ops: (
    { navigateTo, contractKey, talentFullName, companyName, canceledByFullName, previousStatus },
    baseUrl
  ) => ({
    title: `🗑️ Contrat #${contractKey} annulé`,
    body: `${canceledByFullName} a annulé le contrat de ${talentFullName}${companyName ? ` (${companyName})` : ""}.\nStatut précédent : ${translateContractStatus.fr[previousStatus].default}. ${formatLink(navigateTo, "Voir le contrat", baseUrl)}`,
  }),
  talents_merged_with_contract: ({ navigateTo, talentFullName, contractCount, mergedByFullName }, baseUrl) => ({
    title: `🔀 Fusion de talents — ${talentFullName}`,
    body: `${mergedByFullName} a fusionné des fiches talent en doublon. ${contractCount > 1 ? `${contractCount} contrats sont rattachés` : "1 contrat est rattaché"} à la fiche conservée : vérifie que les informations du contrat sont exactes. ${formatLink(navigateTo, "Voir les contrats", baseUrl)}`,
  }),
  talent_identity_document_uploaded: ({ navigateTo, talent }, baseUrl) => {
    const talentFullName = [talent.first_name, talent.last_name].filter(Boolean).join(" ") || EMPTY;
    return {
      title: `🪪 Pièce d'identité mise à jour — ${talentFullName}`,
      body: `Le talent ${talentFullName} vient de mettre à jour sa pièce d'identité.\n\nRends-toi sur le BO pour la consulter et la valider : ${formatLink(navigateTo, "Voir les documents", baseUrl)}`,
    };
  },
  saved_talent_search_daily_summary: ({ searches }) => {
    const searchCount = searches.length;
    const talentCount = searches.reduce((total, search) => total + search.newMatchCount, 0);
    return {
      title: `${talentCount} nouveau${talentCount > 1 ? "x" : ""} talent${talentCount > 1 ? "s" : ""} sur ${searchCount === 1 ? "ta recherche sauvegardée" : `tes ${searchCount} recherches sauvegardées`}`,
      // Genuinely plain: the Customer.io template renders the message and owns the "Voir mes
      // recherches" button, so a link marked up here would surface as raw HTML in the text part.
      body: searches.map((search) => `${search.title} : +${search.newMatchCount}`).join("\n"),
    };
  },
  new_talents_matching_saved_talent_search: ({ navigateTo, savedTalentSearchTitle, newMatchCount }, baseUrl) => {
    const isPlural = newMatchCount > 1;
    return {
      title: `🔍 ${newMatchCount} nouveau${isPlural ? "x" : ""} talent${isPlural ? "s" : ""} ${isPlural ? "correspondent" : "correspond"} à ta recherche ${savedTalentSearchTitle}`,
      body: formatLink(navigateTo, "Voir", baseUrl),
    };
  },
};

// This our main map where we spread the two previous objects.
const notificationTextFormatters: {
  [Type in Database["public"]["Enums"]["notification_type"]]: (
    notificationData: Extract<NotificationData, { type: Type }>["data"],
    baseUrl?: string
  ) => NotificationText;
} = { ...mobileAppNotificationTextFormatters, ...backOfficeNotificationTextFormatters };
